import { refundArrowSVG } from 'assets/svg/arrow'
import { V3Transaction } from 'models/v3-transaction'
import { Button, List, message, Select, Input, InputNumber, Modal } from 'antd'
import { useAppState } from 'state'
import { StopV3Transaction, CancelV3PosPayment, ProceedV3Payment, VoidV3Payment, RefundV3Transaction } from 'services/data-provider/v3-transaction'
import { renderFormatMessage } from 'helpers/intl'
import { useState } from 'react'
import { ListCardWrapper } from 'atom/list'
import { CancelModal } from 'components/modal/CancelModal'
import BulletPoint from 'components/text/BulletPoint'
import { useUserTranslation } from 'hooks/translation/useUserTranslation'
import { useNotifications } from 'hooks/useNotification'

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message
  }
  return fallback
}

const TransactionPanelAction: React.FC<{ transaction: V3Transaction, reloadTransactions: () => void }> = ({ transaction, reloadTransactions }) => {
  const { currentUser } = useAppState()
  const { openSuccessNotification } = useNotifications()
  const {
    walletTransactionRefundModalTitleText,
    walletTransactionRefundModalReasonsDuplicateChargeText,
    walletTransactionRefundModalReasonsIncorrectAmountText,
    walletTransactionRefundModalReasonsCustomerDisputeText,
    walletTransactionRefundModalReasonsOtherText,
    walletTransactionRefundModalCustomReasonLabelText,
    walletTransactionRefundModalCustomReasonPlaceholderText,
  } = useUserTranslation()

  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(false)

  const [cancelPaymentLoading, setCancelPaymentLoading] = useState(false)
  const [cancelPaymentDisabled, setCancelPaymentDisabled] = useState(false)

  const [proceedPaymentLoading, setProceedPaymentLoading] = useState(false)
  const [proceedPaymentDisabled, setProceedPaymentDisabled] = useState(false)

  const [voidPaymentLoading, setVoidPaymentLoading] = useState(false)
  const [isVoidModalVisible, setIsVoidModalVisible] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [voidCustomReason, setVoidCustomReason] = useState('')
  const [voidFormSubmitted, setVoidFormSubmitted] = useState(false)

  const [isRefundModalVisible, setIsRefundModalVisible] = useState(false)
  const [refundType, setRefundType] = useState<'full' | 'partial' | ''>('')
  const [refundReason, setRefundReason] = useState('')
  const [refundCustomReason, setRefundCustomReason] = useState('')
  const [partialSubtotal, setPartialSubtotal] = useState(0)
  const [partialTaxAmount, setPartialTaxAmount] = useState(0)
  const [refundFormSubmitted, setRefundFormSubmitted] = useState(false)
  const [refundLoading, setRefundLoading] = useState(false)

  const handleStopCharging = async () => {
    try {
      setLoading(true)
      // disable the button
      setDisabled(true)
      await StopV3Transaction(transaction)
      message.success('Charging session stopped successfully, Please wait for the transaction to be updated')
      // add a delay here to give some time to the transaction to be updated
      setTimeout(() => {
        reloadTransactions()
        setDisabled(false)
      }, 5000)
    } catch (error) {
      console.error('Failed to stop charging:', error)
      message.error(getErrorMessage(error, 'Failed to stop charging'), 10)
      setDisabled(false)
    } finally {
      setLoading(false)
    }
  }

  // This button is used by Payter transaction only
  const handleCancelPayment = async () => {
    try {
      setCancelPaymentLoading(true)
      setCancelPaymentDisabled(true)
      
      await CancelV3PosPayment(transaction)
      
      message.success('Payment cancelled successfully. Please wait for the transaction to be updated')
      setTimeout(() => {
        reloadTransactions()
        setCancelPaymentDisabled(false)
      }, 5000)
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to cancel payment'))
      setCancelPaymentDisabled(false)
    } finally {
      setCancelPaymentLoading(false)
    }
  }

  const handleProceedPayment = async () => {
    try {
      setProceedPaymentLoading(true)
      setProceedPaymentDisabled(true)
      await ProceedV3Payment(transaction)
      message.success('Payment proceed requested. Please wait for the transaction to be updated')
      setTimeout(() => {
        reloadTransactions()
        setProceedPaymentDisabled(false)
      }, 5000)
    } catch (error) {
      console.error('Failed to proceed payment:', error)
      message.error(getErrorMessage(error, 'Failed to proceed payment'))
      setProceedPaymentDisabled(false)
    } finally {
      setProceedPaymentLoading(false)
    }
  }

  const VOID_REASONS = [
    { value: 'Charger sent wrong meter values', label: 'Charger sent wrong meter values' },
    { value: 'Incorrect pricing calculation', label: 'Incorrect pricing calculation' },
    { value: 'Other', label: 'Other - Please specify' },
  ]
  const MAX_REASON_LENGTH = 255
  const SAFE_TEXT_REGEX = /^[a-zA-Z0-9\s.,!?;:'"()\-/]+$/

  const handleOpenVoidModal = () => {
    setVoidReason('')
    setVoidCustomReason('')
    setVoidFormSubmitted(false)
    setIsVoidModalVisible(true)
  }

  const handleCloseVoidModal = () => {
    setIsVoidModalVisible(false)
  }

  const getVoidReasonText = (): string => {
    return voidReason === 'Other' ? voidCustomReason.trim() : voidReason
  }

  const isVoidReasonValid = (): boolean => {
    if (!voidReason) return false
    if (voidReason === 'Other') {
      const text = voidCustomReason.trim()
      return text.length > 0 && text.length <= MAX_REASON_LENGTH && SAFE_TEXT_REGEX.test(text)
    }
    return true
  }

  const handleVoidSubmit = async () => {
    setVoidFormSubmitted(true)
    if (!isVoidReasonValid()) return
    try {
      setVoidPaymentLoading(true)
      await VoidV3Payment(transaction.id, getVoidReasonText())
      message.success('Payment voided successfully. Please wait for the transaction to be updated')
      setIsVoidModalVisible(false)
      setTimeout(() => reloadTransactions(), 5000)
    } catch (error) {
      console.error('Failed to void payment:', error)
      message.error(getErrorMessage(error, 'Failed to void payment'), 8)
    } finally {
      setVoidPaymentLoading(false)
    }
  }

  const canShowRefundButton =
    transaction.payment &&
    transaction.payment.state === 'settled' &&
    ['wallet', 'stripe'].includes(transaction.payment.paymentGateway?.toLowerCase() ?? '') &&
    (!transaction.refund || transaction.refund.state !== 'succeeded')

  const amountDollars = transaction.financial ? transaction.financial.total / 100 : 0
  const subtotalDollars = transaction.financial ? transaction.financial.subtotal / 100 : 0
  const taxDollars = transaction.financial ? transaction.financial.tax / 100 : 0

  const handleOpenRefundModal = () => {
    setIsRefundModalVisible(true)
    setRefundType('')
    setRefundReason('')
    setRefundCustomReason('')
    setPartialSubtotal(0)
    setPartialTaxAmount(0)
    setRefundFormSubmitted(false)
  }

  const handleCloseRefundModal = () => {
    setIsRefundModalVisible(false)
    setRefundType('')
    setRefundReason('')
    setRefundCustomReason('')
    setPartialSubtotal(0)
    setPartialTaxAmount(0)
    setRefundFormSubmitted(false)
  }

  const handleRefundSubmit = async () => {
    setRefundFormSubmitted(true)
    if (!refundType || (refundType !== 'full' && refundType !== 'partial')) return
    if (!refundReason) return
    if (refundReason === 'other' && !refundCustomReason.trim()) return

    let refundAmountDollars = 0
    if (refundType === 'full') {
      refundAmountDollars = amountDollars
    } else {
      if (partialSubtotal <= 0) return
      if (partialSubtotal > subtotalDollars) return
      if (partialTaxAmount > taxDollars) return
      refundAmountDollars = partialSubtotal + (partialTaxAmount || 0)
      if (refundAmountDollars > amountDollars) return
    }

    setRefundLoading(true)
    try {
      const otherReason = refundReason === 'other' ? refundCustomReason : undefined
      await RefundV3Transaction(transaction.id, {
        refundType,
        reason: refundReason,
        subtotal: refundType === 'partial' && partialSubtotal > 0 ? partialSubtotal : undefined,
        taxAmount: refundType === 'partial' && partialTaxAmount > 0 ? partialTaxAmount : undefined,
        otherReason,
      })
      openSuccessNotification(`Successfully refunded $${refundAmountDollars.toFixed(2)} for transaction ${transaction.id}`)
      handleCloseRefundModal()
      reloadTransactions()
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to refund transaction'))
    } finally {
      setRefundLoading(false)
    }
  }

  // onlye allow admin / whitelabel admin to stop charging
  if (currentUser?.role !== 'admin') {
    return null
  }

  return (
    <ListCardWrapper style={{ paddingTop: '12px', paddingBottom: '0', minWidth: 0 }}>
        {/* This button is used by Payter transaction only */}
        {transaction.payterInfo && 
         transaction.payterInfo.sessionState?.toLowerCase() === 'authorized' && (
          <List.Item style={{ borderBottom: 'none', paddingBottom: '0px', minWidth: 0 }}>
               <Button
                   type="default"
                   disabled={cancelPaymentDisabled}
                   loading={cancelPaymentLoading}
                   onClick={handleCancelPayment}
                   style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                   >
                   {renderFormatMessage('dashboard.transactionPage.cancel-payment', 'Cancel Payment')}
               </Button>
          </List.Item>
        )}

        {transaction.payment?.state === 'pending_assessment' && (
          <List.Item style={{ borderBottom: 'none', paddingBottom: '0px', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
              <Button
                type="primary"
                disabled={proceedPaymentDisabled}
                loading={proceedPaymentLoading}
                onClick={handleProceedPayment}
                style={{ flex: 1 }}
              >
                {renderFormatMessage('dashboard.transactionPage.proceed-payment', 'Proceed Payment')}
              </Button>
              <Button
                danger
                loading={voidPaymentLoading}
                onClick={handleOpenVoidModal}
                style={{ flex: 1 }}
              >
                Void Payment
              </Button>
            </div>
          </List.Item>
        )}

        {['verified', 'starting', 'in_progress'].includes(transaction.currentState) &&
         transaction.ocppTransactionId != null && (
          <List.Item style={{ borderBottom: 'none', paddingBottom: '0px', minWidth: 0 }}>
              <Button
                  type="primary"
                  danger
                  disabled={disabled}
                  loading={loading}
                  onClick={handleStopCharging}
                  style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                  >
                  {renderFormatMessage('dashboard.transactionPage.stop-charging-session', 'Stop Charging Session')}
              </Button>
          </List.Item>
        )}
        {canShowRefundButton && (
          <List.Item style={{ borderBottom: 'none', paddingBottom: '0px', minWidth: 0 }}>
            <Button
              danger
              onClick={handleOpenRefundModal}
              style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
            >
              {renderFormatMessage('dashboard.transactionPage.refund-transaction', 'Refund Transaction')}
            </Button>
          </List.Item>
        )}
        <CancelModal
          visible={isRefundModalVisible}
          onCancel={handleCloseRefundModal}
          onSubmit={handleRefundSubmit}
          submitText="Refund Transaction"
          title={walletTransactionRefundModalTitleText}
          icon={refundArrowSVG}
          width={520}
          description={
            <div>
              <p className="paragraph-01-regular" style={{ marginBottom: '24px' }}>
                Confirm the refund details and reason for processing.
              </p>
              <div style={{ marginBottom: '16px' }}>
                <p className="paragraph-01-regular">
                  You're about to refund Transaction ID {transaction.id}
                </p>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                  Refund type:
                </label>
                <Select
                  value={refundType || undefined}
                  onChange={(value) => setRefundType(value)}
                  style={{ width: '100%' }}
                  placeholder="Select refund type"
                  options={[
                    { value: 'full', label: 'Full refund' },
                    { value: 'partial', label: 'Partial refund' }
                  ]}
                />
              </div>
              {refundType === 'full' && (
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <p className="paragraph-01-regular">
                      The amount of (${amountDollars.toFixed(2)}) will be refunded back to the driver's{' '}
                      <span className="paragraph-01-bold" style={{ textTransform: 'uppercase' }}>
                        {transaction.payment?.paymentGateway?.toLowerCase() === 'stripe' ? 'card' : 'wallet'}
                      </span>
                    </p>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                      Select a reason:
                    </label>
                    <Select
                      value={refundReason}
                      onChange={(value) => {
                        setRefundReason(value)
                        if (value !== 'other') setRefundCustomReason('')
                      }}
                      style={{ width: '100%' }}
                      placeholder="Select a reason"
                      status={refundFormSubmitted && !refundReason ? 'error' : ''}
                      options={[
                        { value: 'duplicate_charge', label: walletTransactionRefundModalReasonsDuplicateChargeText },
                        { value: 'incorrect_amount', label: walletTransactionRefundModalReasonsIncorrectAmountText },
                        { value: 'customer_dispute', label: walletTransactionRefundModalReasonsCustomerDisputeText },
                        { value: 'other', label: walletTransactionRefundModalReasonsOtherText }
                      ]}
                    />
                    {refundReason === 'other' && (
                      <div style={{ marginTop: '16px' }}>
                        <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                          {walletTransactionRefundModalCustomReasonLabelText}
                        </label>
                        <Input
                          value={refundCustomReason}
                          onChange={(e) => setRefundCustomReason(e.target.value)}
                          placeholder={walletTransactionRefundModalCustomReasonPlaceholderText}
                          style={{ width: '100%' }}
                          status={refundFormSubmitted && refundReason === 'other' && !refundCustomReason.trim() ? 'error' : ''}
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <BulletPoint value={
                      transaction.payment?.paymentGateway?.toLowerCase() === 'stripe'
                        ? "Once processed, the payment method will reflect the refund."
                        : "Once processed, the wallet balance will reflect the refund."
                    } />
                    <BulletPoint value="This action cannot be undone." />
                  </div>
                </>
              )}
              {refundType === 'partial' && (
                <>
                  <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <p className="paragraph-01-regular" style={{ marginBottom: '12px' }}>
                      The amount of (${amountDollars.toFixed(2)} {transaction.financial.currency.toUpperCase()}) can be refunded back to the driver's{' '}
                      <span className="paragraph-01-bold" style={{ textTransform: 'lowercase' }}>
                        {transaction.payment?.paymentGateway?.toLowerCase() === 'stripe' ? 'card' : 'wallet'}
                      </span>
                      .
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span className="paragraph-01-regular">Sub-Total:</span>
                        <span className="paragraph-01-regular">${subtotalDollars.toFixed(2)} {transaction.financial.currency.toUpperCase()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span className="paragraph-01-regular">Tax:</span>
                        <span className="paragraph-01-regular">${taxDollars.toFixed(2)} {transaction.financial.currency.toUpperCase()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span className="paragraph-01-regular">Total:</span>
                        <span className="paragraph-01-regular">${amountDollars.toFixed(2)} {transaction.financial.currency.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                      Subtotal:
                    </label>
                    <InputNumber
                      prefix="$"
                      value={partialSubtotal}
                      onChange={(value) => setPartialSubtotal(value || 0)}
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      precision={2}
                      placeholder="0.00"
                      status={
                        refundFormSubmitted && (partialSubtotal <= 0 || partialSubtotal > subtotalDollars)
                          ? 'error'
                          : ''
                      }
                    />
                    {refundFormSubmitted && partialSubtotal > subtotalDollars && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                        Subtotal cannot exceed the original subtotal amount
                      </div>
                    )}
                    {refundFormSubmitted && partialSubtotal <= 0 && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                        Subtotal must be greater than 0
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                      Tax Amount:
                    </label>
                    <InputNumber
                      prefix="$"
                      value={partialTaxAmount}
                      onChange={(value) => setPartialTaxAmount(value || 0)}
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      precision={2}
                      placeholder="0.00"
                      status={refundFormSubmitted && partialTaxAmount > taxDollars ? 'error' : ''}
                    />
                    {refundFormSubmitted && partialTaxAmount > taxDollars && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                        Tax amount cannot exceed the original tax amount
                      </div>
                    )}
                  </div>
                  {refundFormSubmitted && (partialSubtotal + partialTaxAmount) > amountDollars && (
                    <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '16px' }}>
                      Total refund amount cannot exceed the original transaction amount (${amountDollars.toFixed(2)})
                    </div>
                  )}
                  <div style={{ marginBottom: '24px' }}>
                    <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                      Select a reason:
                    </label>
                    <Select
                      value={refundReason}
                      onChange={(value) => {
                        setRefundReason(value)
                        if (value !== 'other') setRefundCustomReason('')
                      }}
                      style={{ width: '100%' }}
                      placeholder="Select a reason"
                      status={refundFormSubmitted && !refundReason ? 'error' : ''}
                      options={[
                        { value: 'duplicate_charge', label: walletTransactionRefundModalReasonsDuplicateChargeText },
                        { value: 'incorrect_amount', label: walletTransactionRefundModalReasonsIncorrectAmountText },
                        { value: 'customer_dispute', label: walletTransactionRefundModalReasonsCustomerDisputeText },
                        { value: 'other', label: walletTransactionRefundModalReasonsOtherText }
                      ]}
                    />
                    {refundReason === 'other' && (
                      <div style={{ marginTop: '16px' }}>
                        <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                          {walletTransactionRefundModalCustomReasonLabelText}
                        </label>
                        <Input
                          value={refundCustomReason}
                          onChange={(e) => setRefundCustomReason(e.target.value)}
                          placeholder={walletTransactionRefundModalCustomReasonPlaceholderText}
                          style={{ width: '100%' }}
                          status={refundFormSubmitted && refundReason === 'other' && !refundCustomReason.trim() ? 'error' : ''}
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <BulletPoint value={
                      transaction.payment?.paymentGateway?.toLowerCase() === 'stripe'
                        ? "Once processed, the payment method will reflect the refund."
                        : "Once processed, the wallet balance will reflect the refund."
                    } />
                    <BulletPoint value="This action cannot be undone." />
                  </div>
                </>
              )}
            </div>
          }
        />
        <Modal
          title="Reason for Voiding Payment"
          visible={isVoidModalVisible}
          onCancel={handleCloseVoidModal}
          destroyOnClose
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseVoidModal} style={{ marginRight: '0px' }}>
                Cancel
              </Button>
              <Button
                loading={voidPaymentLoading}
                onClick={handleVoidSubmit}
                style={{ backgroundColor: '#e85656', borderColor: '#e85656', color: '#000' }}
              >
                Void Payment
              </Button>
            </div>
          }
        >
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Select a reason:
            </label>
            <Select
              value={voidReason || undefined}
              onChange={(value) => {
                setVoidReason(value)
                if (value !== 'Other') setVoidCustomReason('')
              }}
              placeholder="Select a reason"
              style={{ width: '100%' }}
              options={VOID_REASONS}
            />
            {voidFormSubmitted && !voidReason && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                Please select a reason
              </div>
            )}
          </div>
          {voidReason === 'Other' && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                Type a reason:
              </label>
              <Input
                value={voidCustomReason}
                onChange={(e) => {
                  const val = e.target.value.replace(/[\r\n]/g, '')
                  if (val.length <= MAX_REASON_LENGTH) setVoidCustomReason(val)
                }}
                placeholder="Specify a new reason"
                maxLength={MAX_REASON_LENGTH}
              />
              {voidFormSubmitted && !voidCustomReason.trim() && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  Reason is required
                </div>
              )}
              {voidFormSubmitted && voidCustomReason.trim() && !SAFE_TEXT_REGEX.test(voidCustomReason.trim()) && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  Reason contains invalid characters
                </div>
              )}
            </div>
          )}
        </Modal>
    </ListCardWrapper>
  )
}

export default TransactionPanelAction
